import { useMemo, useRef } from "react";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import { CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import type { ProcessedAssignment } from "../type";
import type { ScheduledJob } from "@/hooks/services/pps/handlers/schedule/job-schedule-types";
import {
  SCHEDULE_CONSTANTS,
  OverviewJobSchedule,
  getColWidth,
  getLaneHeight,
  calculateLanesForSpanningView,
  getSpanningBarPosition,
  getJobBarColorForTime,
  getDayBgClass,
  getHolidayName,
  isWeekendDay,
  ZOOM_MIN
} from "../utils";
import {
  SpanningGridLines,
  DayColumnBackdrop,
  ClippedIndicator,
  getAssignmentBarClasses
} from "./schedule-grid-components";
import { ScheduleBarTooltip } from "./schedule-hover-card";
import { JobBarContent } from "./job-schedule";
import { useHolidays } from "@/hooks/use-holidays";

dayjs.extend(isToday);

// Day header height — Figma 128-91915: height=72
const DAY_HEADER_H = 72;

interface WeeklyCalendarGridProps {
  days: Date[];
  jobs: ScheduledJob[];
  groupedByJob: Record<
    string,
    { assignments: ProcessedAssignment[]; laneCount: number }
  >;
  selectedJobIds: string[];
  onJobSelect: (ids: string[]) => void;
  zoomLevel?: number;
  rowLabelHeader?: string;
  rowLabelDescription?: string;
  headerAction?: ReactNode;
  /** When true, shows ONE unified 24h ruler with all (already-filtered) jobs plotted */
  showDetailView?: boolean;
  formatTime: (date: string | null | undefined) => string;
}

export default function WeeklyCalendarGrid({
  days,
  jobs,
  groupedByJob,
  selectedJobIds,
  onJobSelect,
  zoomLevel = ZOOM_MIN,
  rowLabelHeader = "Job Orders",
  rowLabelDescription,
  headerAction,
  showDetailView = false,
  formatTime
}: WeeklyCalendarGridProps) {
  const holidays = useHolidays();
  const leftBodyRef = useRef<HTMLDivElement>(null);
  const rightBodyRef = useRef<HTMLDivElement>(null);
  const isSyncingScroll = useRef(false);

  const leftPanelWidth = SCHEDULE_CONSTANTS.LEFT_PANEL_WIDTH;
  const colWidth = getColWidth("weekly", zoomLevel);
  const GRID_W = (showDetailView ? 48 : 0) + colWidth * days.length;
  const laneHeight = getLaneHeight(zoomLevel);
  const ROW_PAD = SCHEDULE_CONSTANTS.ROW_PAD;
  const JOB_CARD_H = SCHEDULE_CONSTANTS.SIDEBAR_ROW_MIN_HEIGHT;

  const selectedJobSet = useMemo(
    () => new Set(selectedJobIds),
    [selectedJobIds]
  );

  // Detail grid constants (selected job expansion)
  const DETAIL_TOTAL_HOURS = 24;
  const DETAIL_HOUR_H = 40;
  const DETAIL_GRID_H = DETAIL_TOTAL_HOURS * DETAIL_HOUR_H; // 960px
  const WORK_START = SCHEDULE_CONSTANTS.WORK_START_HOUR;
  const WORK_END = SCHEDULE_CONSTANTS.WORK_END_HOUR;

  // Pre-compute row heights and lane data per job
  const rowMetas = useMemo(
    () =>
      jobs.map((job) => {
        const jobNo = job.jobOrderNo;
        const jobData = groupedByJob[jobNo];

        if (!jobData) {
          return {
            jobNo,
            rowHeight: JOB_CARD_H,
            assignmentsWithLanes: [],
            maxLane: 0
          };
        }

        const { assignments: assignmentsWithLanes, maxLane } =
          calculateLanesForSpanningView(jobData.assignments);

        const rowHeight = Math.max(
          (maxLane + 1) * laneHeight + ROW_PAD * 2,
          laneHeight + ROW_PAD * 2,
          JOB_CARD_H
        );

        return { jobNo, rowHeight, assignmentsWithLanes, maxLane };
      }),
    [jobs, groupedByJob, JOB_CARD_H, laneHeight, ROW_PAD]
  );

  return (
    <CardContent className="aero:p-0">
      {/* ── Two-column flex layout: LEFT panel | RIGHT calendar ───────── */}
      <div
        className="aero:flex aero:overflow-hidden"
        style={{ height: "calc(100vh - 320px)" }}
      >
        {/* ── LEFT COLUMN: header + independent scrolling job list ─────── */}
        <div
          className="aero:flex-shrink-0 aero:flex aero:flex-col aero:h-full aero:border-r aero:border-neutral-300"
          style={{ width: leftPanelWidth }}
        >
          {/* Left header — Figma 193-95290: h=92, py=12, px=20, gap=4, border top+bottom */}
          <div
            className="aero:flex-shrink-0 aero:bg-neutral-50 aero:border-t aero:border-b aero:border-neutral-300 aero:py-3 aero:px-5 aero:flex aero:flex-col aero:gap-1 aero:justify-center"
            style={{ height: 92 }}
          >
            <div className="aero:flex aero:items-center aero:justify-between">
              <span className="aero:font-bold aero:text-sm aero:text-neutral-500 aero:leading-5">
                {rowLabelHeader}
              </span>
              {headerAction}
            </div>
            {rowLabelDescription && (
              <p className="aero:text-[10px] aero:font-normal aero:text-neutral-500 aero:leading-4">
                {rowLabelDescription}
              </p>
            )}
          </div>

          {/* Job list — synced vertical scroll with right body */}
          <div
            ref={leftBodyRef}
            className="aero:flex-1 aero:overflow-y-auto aero:overflow-x-hidden"
            onScroll={() => {
              if (isSyncingScroll.current) return;
              isSyncingScroll.current = true;
              if (rightBodyRef.current && leftBodyRef.current) {
                rightBodyRef.current.scrollTop = leftBodyRef.current.scrollTop;
              }
              requestAnimationFrame(() => {
                isSyncingScroll.current = false;
              });
            }}
          >
            {rowMetas.map(({ jobNo, rowHeight }, i) => {
              const job = jobs[i];
              return (
                <div
                  key={jobNo}
                  className="aero:border-b aero:border-neutral-300 aero:last:border-b-0"
                  style={{ height: rowHeight }}
                >
                  {OverviewJobSchedule(
                    job,
                    groupedByJob[jobNo]?.assignments ?? [],
                    jobNo,
                    () => {
                      if (selectedJobSet.has(jobNo)) {
                        onJobSelect(
                          selectedJobIds.filter((id) => id !== jobNo)
                        );
                      } else {
                        onJobSelect([...selectedJobIds, jobNo]);
                      }
                    },
                    selectedJobSet.has(jobNo)
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── RIGHT COLUMN: day-name header + independent scrolling calendar ── */}
        <div className="aero:flex-1 aero:flex aero:flex-col aero:h-full aero:overflow-hidden aero:pl-5 aero:pt-5">
          <div
            ref={rightBodyRef}
            className="aero:flex-1 aero:overflow-auto"
            onScroll={() => {
              if (isSyncingScroll.current) return;
              isSyncingScroll.current = true;
              if (leftBodyRef.current && rightBodyRef.current) {
                leftBodyRef.current.scrollTop = rightBodyRef.current.scrollTop;
              }
              requestAnimationFrame(() => {
                isSyncingScroll.current = false;
              });
            }}
          >
            <div
              className="aero:w-full aero:relative aero:min-h-full"
              style={{ minWidth: showDetailView ? GRID_W : undefined }}
            >
              {/* Full-height weekend/holiday column backdrop behind all rows (WOR-2187 R1) */}
              {!showDetailView && <DayColumnBackdrop days={days} />}
              {/* Sticky day header */}
              <div
                className="aero:sticky aero:top-0 aero:z-10 aero:bg-white aero:border-b aero:border-t aero:border-neutral-300"
                style={{ height: DAY_HEADER_H }}
              >
                <div className="aero:flex aero:h-full aero:w-full">
                  {showDetailView && (
                    <div
                      className="aero:flex-shrink-0 aero:border-r aero:border-neutral-200"
                      style={{ width: 48 }}
                    />
                  )}
                  {days.map((day) => {
                    const isTodayDay = dayjs(day).isToday();
                    const holidayName = getHolidayName(day, holidays);
                    const isHoliday = !!holidayName;
                    const isWeekend = isWeekendDay(day);
                    return (
                      <div
                        key={day.toISOString()}
                        className={cn(
                          "aero:pt-1 aero:pr-2 aero:pb-4 aero:pl-2 aero:border-r aero:border-neutral-300 aero:last:border-r-0",
                          // Precedence today > holiday > weekend (WOR-2187).
                          isTodayDay
                            ? "aero:bg-primary/10"
                            : isHoliday
                              ? "aero:bg-yellow-200"
                              : isWeekend
                                ? "aero:bg-gray-200"
                                : ""
                        )}
                        style={
                          showDetailView
                            ? { width: colWidth }
                            : { flex: 1, minWidth: colWidth }
                        }
                      >
                        <p
                          className={cn(
                            "aero:text-[10px] aero:font-semibold aero:uppercase aero:tracking-widest aero:leading-none aero:mb-1",
                            isTodayDay
                              ? "aero:text-primary"
                              : "aero:text-muted-foreground"
                          )}
                        >
                          {dayjs(day).format("ddd")}
                        </p>
                        <p
                          className={cn(
                            "aero:text-xl aero:font-bold aero:leading-none",
                            isTodayDay
                              ? "aero:text-primary"
                              : "aero:text-foreground"
                          )}
                        >
                          {dayjs(day).format("D")}
                        </p>
                        {isHoliday && (
                          <p
                            className="aero:mt-1 aero:text-[10px] aero:font-medium aero:leading-tight aero:text-amber-700 aero:truncate"
                            title={holidayName}
                          >
                            {holidayName}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Calendar body — unified detail view (applied job selection) */}
              {showDetailView && (
                <div className="aero:flex" style={{ height: DETAIL_GRID_H }}>
                  {/* ONE time ruler — inline, not sticky */}
                  <div
                    className="aero:flex-shrink-0 aero:bg-white aero:border-r aero:border-neutral-200"
                    style={{
                      width: 48,
                      height: DETAIL_GRID_H,
                      position: "relative"
                    }}
                  >
                    {Array.from({ length: DETAIL_TOTAL_HOURS }, (_, h) => (
                      <span
                        key={h}
                        className="aero:absolute aero:text-[9px] aero:leading-none aero:text-neutral-400 aero:select-none"
                        style={{
                          top: Math.max(h * DETAIL_HOUR_H - 5, 0),
                          left: 4,
                          width: 36
                        }}
                      >
                        {`${String(h).padStart(2, "0")}:00`}
                      </span>
                    ))}
                  </div>
                  {/* Day columns — all selected jobs' assignments in one unified 960px canvas */}
                  {days.map((day) => {
                    const dayStart = dayjs(day).startOf("day");
                    const dayEnd = dayjs(day).endOf("day");
                    const allDayAssignments = rowMetas.flatMap(
                      ({ assignmentsWithLanes }) =>
                        assignmentsWithLanes.filter((a) => {
                          if (!a.startDate || !a.eddDate) return false;
                          return (
                            !dayjs(a.startDate).isAfter(dayEnd) &&
                            !dayjs(a.eddDate).isBefore(dayStart)
                          );
                        })
                    );
                    return (
                      <div
                        key={day.toISOString()}
                        className={cn(
                          "aero:relative aero:flex-shrink-0 aero:border-r aero:border-neutral-300",
                          // Weekend grey / holiday yellow / today in the 100%-zoom detail grid (WOR-2187).
                          getDayBgClass(day, holidays)
                        )}
                        style={{ width: colWidth, height: DETAIL_GRID_H }}
                      >
                        {Array.from({ length: DETAIL_TOTAL_HOURS }, (_, hi) => (
                          <div
                            key={hi}
                            className={cn(
                              "aero:absolute aero:inset-x-0 aero:border-t aero:z-[1]",
                              hi >= WORK_START && hi < WORK_END
                                ? "aero:border-neutral-300"
                                : "aero:border-neutral-200"
                            )}
                            style={{ top: hi * DETAIL_HOUR_H }}
                          />
                        ))}
                        {allDayAssignments.map((a, aIdx) => {
                          const startD = dayjs(a.startDate);
                          const endD = dayjs(a.eddDate);
                          const startH = startD.isSame(day, "day")
                            ? startD.hour() + startD.minute() / 60
                            : 0;
                          const endH = endD.isSame(day, "day")
                            ? endD.hour() + endD.minute() / 60
                            : 24;
                          if (endH <= startH) return null;
                          const top = startH * DETAIL_HOUR_H;
                          const barH = Math.max(
                            (endH - startH) * DETAIL_HOUR_H,
                            24
                          );
                          const {
                            color: barColor,
                            text: barText,
                            accent: barAccent
                          } = getJobBarColorForTime(a.startDate, a.barVariant);
                          return (
                            <ScheduleBarTooltip
                              key={`${a.id}-${day.toISOString()}-${aIdx}`}
                              variant="job"
                              data={{
                                jobOrderNo: a.jobOrderNo,
                                processDesc: a.processDesc,
                                partNo: a.partNo,
                                partDescription: a.partDescription,
                                customerName: (a as ProcessedAssignment)
                                  .customerName,
                                startDate: a.startDate,
                                eddDate: a.eddDate,
                                duration: a.duration,
                                technicianName: a.technicianName,
                                machineName: a.machineName,
                                priorityColor: barColor,
                                unavailableReason: a.barUnavailableReason
                              }}
                            >
                              <div
                                className={cn(
                                  "aero:absolute aero:inset-x-1 aero:rounded aero:border aero:border-dashed aero:z-[3] aero:overflow-hidden aero:px-2 aero:pt-1",
                                  barColor
                                )}
                                style={{ top, height: barH }}
                              >
                                <div
                                  className={cn(
                                    "aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] aero:rounded-l",
                                    barAccent
                                  )}
                                />
                                <p
                                  className={cn(
                                    "aero:text-[10px] aero:truncate aero:leading-[14px]",
                                    barText
                                  )}
                                >
                                  {formatTime(a.startDate)}&nbsp;–&nbsp;
                                  {formatTime(a.eddDate)}
                                </p>
                                {barH >= 36 && (
                                  <p className="aero:text-xs aero:font-bold aero:truncate aero:leading-[14px]">
                                    {a.processDesc}
                                  </p>
                                )}
                                {barH >= 60 && (
                                  <p
                                    className={cn(
                                      "aero:text-[10px] aero:truncate aero:leading-[14px]",
                                      barText
                                    )}
                                  >
                                    {a.jobOrderNo}
                                  </p>
                                )}
                              </div>
                            </ScheduleBarTooltip>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              )}
              {/* Calendar body rows — normal spanning view */}
              {!showDetailView &&
                rowMetas.map(({ jobNo, rowHeight, assignmentsWithLanes }) => (
                  <div
                    key={jobNo}
                    className="aero:relative aero:border-b aero:border-neutral-300 aero:last:border-b-0"
                    style={{ height: rowHeight }}
                  >
                    <SpanningGridLines days={days} viewMode="weekly" />
                    {assignmentsWithLanes.map((assignment) => {
                      const barPos = getSpanningBarPosition(
                        assignment,
                        days,
                        "weekly",
                        zoomLevel
                      );
                      if (!barPos || !barPos.isVisible) return null;

                      const {
                        leftPosition,
                        barWidthStyle,
                        isStartClipped,
                        isEndClipped
                      } = barPos;
                      const lane = assignment.lane || 0;
                      const topOffset = ROW_PAD + lane * laneHeight;
                      const barHeight = laneHeight - ROW_PAD;
                      const {
                        color: barColor,
                        text: barText,
                        accent: barAccent
                      } = getJobBarColorForTime(
                        assignment.startDate,
                        assignment.barVariant
                      );

                      return (
                        <ScheduleBarTooltip
                          key={assignment.id}
                          variant="job"
                          data={{
                            jobOrderNo: assignment.jobOrderNo,
                            processDesc: assignment.processDesc,
                            partNo: assignment.partNo,
                            partDescription: assignment.partDescription,
                            customerName: (assignment as ProcessedAssignment)
                              .customerName,
                            startDate: assignment.startDate,
                            eddDate: assignment.eddDate,
                            duration: assignment.duration,
                            technicianName: assignment.technicianName,
                            machineName: assignment.machineName,
                            priorityColor: barColor,
                            unavailableReason: assignment.barUnavailableReason
                          }}
                        >
                          <div
                            className={getAssignmentBarClasses(
                              barColor,
                              isStartClipped,
                              isEndClipped,
                              false,
                              barText,
                              "dashed-full"
                            )}
                            style={{
                              left: leftPosition,
                              width: barWidthStyle,
                              top: `${topOffset}px`,
                              height: `${barHeight}px`
                            }}
                          >
                            {isStartClipped && (
                              <ClippedIndicator position="start" />
                            )}
                            {isEndClipped && (
                              <ClippedIndicator position="end" />
                            )}
                            {!isStartClipped && (
                              <div
                                className={`aero:absolute aero:left-0 aero:top-0 aero:bottom-0 aero:w-[3px] ${barAccent} aero:rounded-l`}
                              />
                            )}
                            <JobBarContent
                              startDate={assignment.startDate}
                              eddDate={assignment.eddDate}
                              duration={assignment.duration}
                              processDesc={assignment.processDesc}
                              jobOrderNo={assignment.jobOrderNo}
                              partNo={assignment.partNo}
                              partDescription={assignment.partDescription}
                              customerName={
                                (assignment as ProcessedAssignment).customerName
                              }
                              technicianName={assignment.technicianName}
                              machineName={assignment.machineName}
                              barHeight={barHeight}
                              showDaySpan
                              textClass={barText}
                              formatTime={formatTime}
                            />
                          </div>
                        </ScheduleBarTooltip>
                      );
                    })}
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </CardContent>
  );
}
